import type {
  InvoiceOwnerCompanySuggestionsQueryParams,
  InvoiceOwnerCompanySuggestionsResponse,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import {
  zodInvoiceOwnerCompanySuggestionsQueryParamsSchema,
  zodInvoiceOwnerCompanySuggestionsResponseSchema,
} from './validations';

/**
 * Fetches the companies the user may set as owner of an imported invoice,
 * i.e. the ones they hold `invoice.write` on. Inactive companies are left out;
 * a user without any such company gets an empty list.
 *
 * @param queryParams - Optional case-insensitive name substring and a result limit (1–50, API default 10).
 * @param options - Additional request options (e.g. abort signal).
 * @returns The matching owner companies, ordered by name.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/suggest_owner_companies_invoices_suggestions_owner_companies_get
 */
export const fetchInvoiceOwnerCompanySuggestions = async (
  queryParams: Partial<InvoiceOwnerCompanySuggestionsQueryParams>,
  options?: KyRequestOptions,
) => {
  return await vestigasApi
    .get(ENDPOINT.INVOICE.GET_OWNER_COMPANY_SUGGESTIONS(), {
      searchParams: queryParams,
      ...options,
      validate: {
        ...options?.validate,
        response: zodInvoiceOwnerCompanySuggestionsResponseSchema,
        searchParams:
          zodInvoiceOwnerCompanySuggestionsQueryParamsSchema.transform((data) =>
            withoutObjectKeysWhereValueIs(data, [undefined, '']),
          ),
      },
    })
    .json<InvoiceOwnerCompanySuggestionsResponse>();
};
